
import { defineComponent, ref, computed, watch, PropType } from 'vue'
const __default__ = defineComponent({
  name: 'TradingRules',
  props: {
    isLight: Boolean,
    colors: { type: Object as PropType<Record<string, string>>, required: true },
    comOptions: { type: Object as PropType<any>, default: null },
    initialValue: { type: Object as PropType<any>, default: null }
  },
  emits: ['change'],
  setup(props, { emit }) {
    // buyPriorities 存的是 index（对应 buyPriorityOptions 的下标），方便同时取 priority+direction
    const buyPriorities = ref<string[]>([])
    const holdDays = ref<string[]>(['5'])
    const customHoldDay = ref('')
    const indicators = ref<string[]>([])
    const selectedIndicatorOptions = ref<Record<string, string>>({})
    const stopProfit = ref('')
    const stopLoss = ref('')
    const stockLoss = ref('')
    const maxDrawdown = ref('')
    const stopErrors = ref({ profit: '', loss: '', stock: '', drawdown: '' })

    const validateStop = (field: keyof typeof stopErrors.value, val: string) => {
      if (!val) { stopErrors.value[field] = ''; return }
      const v = Number(val)
      if (isNaN(v) || v <= 0 || v > 100) {
        stopErrors.value[field] = '请输入 (0,100] 范围内的数字'
      } else if (!/^\d+(\.\d{0,2})?$/.test(val)) {
        stopErrors.value[field] = '最多支持小数点后两位'
      } else {
        stopErrors.value[field] = ''
      }
    }

    const onStopLossInput = () => {
      if (stopLoss.value.startsWith('-')) {
        stopLoss.value = stopLoss.value.replace(/^-+/, '')
      }
      validateStop('loss', stopLoss.value)
    }
    const sellTime = ref('next_open')
    const buyPriorityLabel = computed(() => {
      return buyPriorities.value.map(v => {
        const opt = buyPriorityOptions.value.find(o => o.value === v)
        return opt ? opt.label : ''
      }).join('、')
    })

    const holdDaysLabel = computed(() => {
      return holdDays.value.map(v => {
        if (v === 'custom') return customHoldDay.value ? `${customHoldDay.value}天` : '自定义'
        const opt = holdDayOptions.value.find(o => o.value === v)
        return opt ? opt.label : v
      }).join('、')
    })

    const indicatorsLabel = computed(() => {
      return indicators.value.map(v => {
        const opt = indicatorOptions.value.find((o: any) => o.value === v)
        const subName = selectedIndicatorOptions.value[v]
          ? getOptionName(v, selectedIndicatorOptions.value[v])
          : ''
        return opt ? (subName ? `${opt.label}:${subName}` : opt.label) : v
      }).join('，')
    })

    const emitChange = () => {
      // 持股周期：取第一个有效值，custom 时用 customHoldDay
      const firstHold = holdDays.value[0]
      const holdPeriod = firstHold === 'custom'
        ? (customHoldDay.value ? parseInt(customHoldDay.value) : null)
        : (firstHold ? parseInt(firstHold) : null)

      // 买入优先级：从 buyPriorityOptions 取完整的 priority + direction
      const selectedPriorityOpt = buyPriorityOptions.value.find(o => o.value === buyPriorities.value[0])
      const buyPriorityCode = selectedPriorityOpt?.buyPriority || ''
      const buyPriorityDirection = selectedPriorityOpt?.buyPriorityDirection || ''

      // 卖出指标信号：用用户选中的 optionCode，没选则取第一个
      const sellOptions = indicators.value.map(v => {
        const sellItem = sellListOptions.value.find((s: any) => s.factorCode === v)
        const selectedOption = selectedIndicatorOptions.value[v]
        const optionCode = selectedOption || sellItem?.options?.[0]?.optionCode
        return {
          factorCode: v,
          factorType: sellItem?.factorSelectType ?? 2,
          options: optionCode ? [{ optionCode }] : []
        }
      })

      // sellTime: T+1开盘=1, T+1收盘=2
      const sellTimeInt = sellTime.value === 'next_open' ? 1 : 2

      // 止盈止损：用户输入百分比 → 除以100转小数，空值传 null
      const toRate = (v: string) => v ? parseFloat((parseFloat(v) / 100).toFixed(4)) : null

      // 直接校验当前输入值，不依赖 stopErrors 异步状态
      const isValidStop = (v: string) => {
        if (!v) return true
        const n = Number(v)
        return !isNaN(n) && n > 0 && n <= 100 && /^\d+(\.\d{0,2})?$/.test(v)
      }
      const hasStopError = !isValidStop(stopProfit.value) || !isValidStop(stopLoss.value)
        || !isValidStop(stockLoss.value) || !isValidStop(maxDrawdown.value)
      // 持股周期至少选一个
      const hasHoldPeriod = holdDays.value.length > 0 && holdPeriod !== null
      // 买入优先级至少选一个
      const hasBuyPriority = buyPriorities.value.length > 0 && !!buyPriorityCode
      const valid = !hasStopError && hasHoldPeriod && hasBuyPriority

      // 卖出时间展示文字
      const sellTimeLabel = sellTime.value === 'next_open' ? 'T+1开盘' : 'T+1收盘'

      emit('change', {
        // 展示用
        buyPriorityLabel: buyPriorityLabel.value,
        holdDaysLabel: holdDaysLabel.value,
        indicatorsLabel: indicatorsLabel.value,
        sellTimeLabel,
        stopProfit: stopProfit.value,
        stopLoss: stopLoss.value,
        stockLoss: stockLoss.value,
        maxDrawdown: maxDrawdown.value,
        // 接口字段
        buyPriority: buyPriorityCode,
        buyPriorityDirection,
        holdPeriod,
        sellOptions,
        takeProfit: toRate(stopProfit.value),
        sellStopLoss: stopLoss.value
          ? -Math.abs(parseFloat((parseFloat(stopLoss.value) / 100).toFixed(4)))
          : null,
        sellStockReturn: toRate(stockLoss.value),
        sellFallenBack: toRate(maxDrawdown.value),
        sellTime: sellTimeInt,
        buyTime: 1,
        valid,
      })
    }

    watch([buyPriorities, holdDays, indicators, selectedIndicatorOptions, stopProfit, stopLoss, stockLoss, maxDrawdown, sellTime, customHoldDay], emitChange, { deep: true })

    // 口令回显：当父组件传入 initialValue 时，将接口数据映射回本地状态
    watch(() => props.initialValue, (val) => {
      if (!val) return
      // 买入优先级：用 buyPriority + buyPriorityDirection 拼出 key
      if (val.buyPriority) {
        buyPriorities.value = [`${val.buyPriority}_${val.buyPriorityDirection || ''}`]
      }
      // 持股周期
      if (val.holdPeriod) {
        const std = ['5', '30', '60', '90', '120', '180']
        const s = String(val.holdPeriod)
        if (std.includes(s)) {
          holdDays.value = [s]
        } else {
          holdDays.value = ['custom']
          customHoldDay.value = s
        }
      }
      // 卖出指标
      if (val.sellOptions?.length) {
        indicators.value = val.sellOptions.map((o: any) => o.factorCode)
        // 恢复每个指标选中的 optionCode
        val.sellOptions.forEach((o: any) => {
          if (o.options?.length) {
            selectedIndicatorOptions.value[o.factorCode] = o.options[0].optionCode
          }
        })
      }
      // 止盈止损（小数 → 百分比字符串）
      const toPercent = (v: number | null) => v != null ? String(parseFloat((v * 100).toFixed(2))) : ''
      stopProfit.value = toPercent(val.takeProfit)
      stopLoss.value = val.sellStopLoss != null
        ? String(Math.abs(parseFloat((Number(val.sellStopLoss) * 100).toFixed(2))))
        : ''
      stockLoss.value = toPercent(val.sellStockReturn)
      maxDrawdown.value = toPercent(val.sellFallenBack)
      // 卖出时机固定为 T+1开盘，不随口令回显覆盖
      // sellTime.value = val.sellTime === 2 ? 'next_close' : 'next_open'
    }, { immediate: true })

    const buyPriorityOptions = computed(() => {
      if (props.comOptions?.buyPriorityList?.length) {
        return props.comOptions.buyPriorityList.map((o: any) => ({
          label: o.buyPriorityName || o.name || '',
          value: (o.buyPriority || '') + '_' + (o.buyPriorityDirection || ''), // 唯一key
          buyPriority: o.buyPriority || '',
          buyPriorityDirection: o.buyPriorityDirection || '',
        }))
      }
      // 降级静态数据，value 格式统一为 factorCode_direction
      return [
        { label: '总市值从大到小', value: 'factor_market_value_降序', buyPriority: 'factor_market_value', buyPriorityDirection: '降序' },
        { label: '总市值从小到大', value: 'factor_market_value_升序', buyPriority: 'factor_market_value', buyPriorityDirection: '升序' },
        { label: '涨跌幅从大到小', value: 'hq_pxchange_降序', buyPriority: 'hq_pxchange', buyPriorityDirection: '降序' },
        { label: '涨跌幅从小到大', value: 'hq_pxchange_升序', buyPriority: 'hq_pxchange', buyPriorityDirection: '升序' },
        { label: '换手率从大到小', value: 'turnover_rate_降序', buyPriority: 'turnover_rate', buyPriorityDirection: '降序' },
        { label: '换手率从小到大', value: 'turnover_rate_升序', buyPriority: 'turnover_rate', buyPriorityDirection: '升序' },
        { label: '成交额从大到小', value: 'bussiness_amount_降序', buyPriority: 'bussiness_amount', buyPriorityDirection: '降序' },
        { label: '成交额从小到大', value: 'bussiness_amount_升序', buyPriority: 'bussiness_amount', buyPriorityDirection: '升序' },
        { label: '成交量从大到小', value: 'bussiness_volume_降序', buyPriority: 'bussiness_volume', buyPriorityDirection: '降序' },
        { label: '成交量从小到大', value: 'bussiness_volume_升序', buyPriority: 'bussiness_volume', buyPriorityDirection: '升序' },
        { label: '量比从大到小', value: 'quantity_ratio_降序', buyPriority: 'quantity_ratio', buyPriorityDirection: '降序' },
        { label: '量比从小到大', value: 'quantity_ratio_升序', buyPriority: 'quantity_ratio', buyPriorityDirection: '升序' },
      ]
    })

    // 买入优先级默认选中"涨跌幅从大到小"，等选项加载后设置
    watch(buyPriorityOptions, (opts) => {
      if (buyPriorities.value.length > 0) return // 已有选中（口令回显）则不覆盖
      const defaultOpt = opts.find(o => o.buyPriority === 'hq_pxchange' && o.buyPriorityDirection === '降序')
      if (defaultOpt) buyPriorities.value = [defaultOpt.value]
    }, { immediate: true })

    const holdDayOptions = computed(() => {
      if (props.comOptions?.holdDayList?.length) {
        return props.comOptions.holdDayList.map((o: any) => ({
          label: o.name,
          value: String(o.day),
        }))
      }
      return [
        { label: '5天', value: '5' },
        { label: '30天', value: '30' },
        { label: '60天', value: '60' },
        { label: '90天', value: '90' },
        { label: '120天', value: '120' },
        { label: '180天', value: '180' },
      ]
    })

    // 从 comOptions.sellList 取真实卖出指标列表
    const sellListOptions = computed(() => props.comOptions?.sellList || [])

    const indicatorOptions = computed(() => {
      if (sellListOptions.value.length) {
        return sellListOptions.value.map((s: any) => ({
          label: s.factorName || '',
          value: s.factorCode || '',
          sub: s.factorDescript || '',
          options: s.options || [],
        }))
      }
      return [
        { label: 'MACD', value: 'factor_macd', sub: '趋势指标', options: [] },
        { label: 'BOLL', value: 'factor_boll', sub: '布林带', options: [] },
        { label: 'BBI',  value: 'factor_bbi',  sub: '多空指标', options: [] },
        { label: 'MA',   value: 'factor_ma',   sub: '均线', options: [] },
        { label: 'KDJ',  value: 'factor_kdj',  sub: '超买超卖', options: [] },
        { label: 'RSI',  value: 'factor_rsi',  sub: '弱势指标', options: [] },
      ]
    })

    const sellTimeOptions = [
      { label: 'T+1开盘', value: 'next_open' },
      { label: 'T+1收盘', value: 'next_close' },
    ]

    const toggleBuyPriority = (v: string) => {
      if (buyPriorities.value[0] === v) {
        buyPriorities.value = []
      } else {
        buyPriorities.value = [v]
      }
    }

    const toggleHoldDay = (v: string) => {
      if (holdDays.value[0] === v) {
        holdDays.value = []
      } else {
        holdDays.value = [v]
        if (v !== 'custom') customHoldDay.value = ''
      }
    }

    const toggleIndicator = (v: string) => {
      const idx = indicators.value.indexOf(v)
      if (idx >= 0) {
        // 再次点击取消选中
        indicators.value = []
        selectedIndicatorOptions.value = {}
      } else {
        // 单选：替换之前选中的
        indicators.value = [v]
        const opt = indicatorOptions.value.find((o: any) => o.value === v)
        selectedIndicatorOptions.value = opt?.options?.length
          ? { [v]: opt.options[0].optionCode }
          : {}
      }
    }

    const removeIndicator = (v: string) => {
      const idx = indicators.value.indexOf(v)
      if (idx >= 0) indicators.value.splice(idx, 1)
      const next = { ...selectedIndicatorOptions.value }
      delete next[v]
      selectedIndicatorOptions.value = next
    }

    const clearIndicators = () => {
      indicators.value = []
      selectedIndicatorOptions.value = {}
    }

    // 浮层状态
    const indicatorPopup = ref<{
      visible: boolean
      factorCode: string
      label: string
      options: any[]
      selected: string[]
      x: number
      y: number
    }>({ visible: false, factorCode: '', label: '', options: [], selected: [], x: 0, y: 0 })

    const openIndicatorPopup = (opt: any, e: MouseEvent) => {
      if (!opt.options?.length) {
        // 无子选项直接 toggle
        toggleIndicator(opt.value)
        return
      }
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
      const x = Math.min(rect.left, window.innerWidth - 320)
      const y = rect.bottom + 6 + window.scrollY
      indicatorPopup.value = {
        visible: true,
        factorCode: opt.value,
        label: opt.label,
        options: opt.options,
        selected: selectedIndicatorOptions.value[opt.value]
          ? [selectedIndicatorOptions.value[opt.value]]
          : [],
        x,
        y,
      }
    }

    const togglePopupOption = (optionCode: string) => {
      // 单选：已选则取消，未选则替换
      const sel = indicatorPopup.value.selected
      if (sel.includes(optionCode)) {
        indicatorPopup.value.selected = []
      } else {
        indicatorPopup.value.selected = [optionCode]
      }
    }

    const cancelIndicatorPopup = () => {
      indicatorPopup.value.visible = false
    }

    const canConfirmIndicator = computed(
      () => indicatorPopup.value.visible && indicatorPopup.value.selected.length > 0,
    )

    const confirmIndicatorPopup = () => {
      const { factorCode, selected } = indicatorPopup.value
      if (selected.length === 0) return
      indicators.value = [factorCode]
      selectedIndicatorOptions.value = { [factorCode]: selected[0] }
      indicatorPopup.value.visible = false
    }

    const selectIndicatorOption = (factorCode: string, optionCode: string) => {
      selectedIndicatorOptions.value[factorCode] = optionCode
    }

    const getOptionName = (factorCode: string, optionCode: string) => {
      const opt = indicatorOptions.value.find((o: any) => o.value === factorCode)
      return opt?.options?.find((o: any) => o.optionCode === optionCode)?.optionName || optionCode
    }

    const tagStyle = (selected: boolean) => ({
      background: selected ? '#FFEDED' : (props.isLight ? '#f0f0f0' : '#2a2a2a'),
      color: selected ? '#1a1a1a' : props.colors.textPrimary,
      borderColor: 'transparent',
    })

    const optionStyle = (active: boolean) => ({
      background: active ? '#FFEDED' : (props.isLight ? '#f5f5f5' : '#333'),
      color: active ? '#1a1a1a' : props.colors.textPrimary,
      borderColor: active ? '#e8392a' : 'transparent',
      cursor: 'pointer'
    })

    const inputStyle = computed(() => ({
      background: props.isLight ? '#f5f5f5' : '#2a2a2a',
      color: props.colors.textPrimary,
      borderColor: props.colors.border,
    }))

    const INDICATOR_DESC_MAX = 6
    const isIndicatorDescOverflow = (text?: string) => (text || '').length > INDICATOR_DESC_MAX
    const formatIndicatorDesc = (text?: string) => {
      const s = text || ''
      return isIndicatorDescOverflow(s) ? `${s.slice(0, INDICATOR_DESC_MAX)}...` : s
    }

    return {
      buyPriorities,
      holdDays, customHoldDay, indicators, selectedIndicatorOptions,
      stopProfit, stopLoss, stockLoss, maxDrawdown, sellTime,
      stopErrors, validateStop, onStopLossInput,
      buyPriorityOptions, holdDayOptions, indicatorOptions, sellTimeOptions,
      toggleBuyPriority, toggleHoldDay, toggleIndicator, removeIndicator, clearIndicators,
      indicatorPopup, canConfirmIndicator, openIndicatorPopup, togglePopupOption, cancelIndicatorPopup, confirmIndicatorPopup,
      getOptionName,
      isIndicatorDescOverflow, formatIndicatorDesc,
      tagStyle, optionStyle, inputStyle
    }
  }
})

import { useCssVars as _useCssVars } from 'vue'
const __injectCSSVars__ = () => {
_useCssVars(_ctx => ({
  "77ccfdb2": (_ctx.isLight ? "#f0f2f5" : "rgba(255,255,255,0.07)"),
  "3147d2c6": (_ctx.isLight ? "#000" : "#fff")
}))}
const __setup__ = __default__.setup
__default__.setup = __setup__
  ? (props, ctx) => { __injectCSSVars__();return __setup__(props, ctx) }
  : __injectCSSVars__

export default __default__